// Atrionos staff (Super Admin) operations across associations: the switcher, the
// Associations panel, onboarding leads, public-records parcels, provisioning.
// Every table and RPC used here is staff-only under RLS.
import { supabase } from '../lib/supabase';
import { tenantPortalUrl } from '../lib/tenant';

// supabase.functions.invoke hides the JSON error body behind error.context.
async function invokeFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return data;
  // FunctionsFetchError: the request never got an answer (function not deployed, offline).
  let message = error.name === 'FunctionsFetchError'
    ? `Could not reach the ${name} service. Check your connection and try again.`
    : error.message || `${name} failed`;
  try {
    const payload = await error.context?.json?.();
    if (payload?.error) message = payload.error;
  } catch (_) {
    // Non-JSON error body: keep the generic message.
  }
  throw new Error(message);
}

export const associationService = {
  async listAssociations() {
    const { data, error } = await supabase.rpc('list_associations_for_staff');
    if (error) throw error;
    return data || [];
  },

  async listLeads() {
    const { data, error } = await supabase
      .from('onboarding_leads')
      .select('id, created_at, source, organization, contact_name, contact_email, plan, community_address, status, research_status, research_summary, research_error, hoa_id, provisioning')
      .order('created_at', { ascending: false })
      .range(0, 49);
    if (error) throw error;
    return data || [];
  },

  async createLead({ organization, communityAddress, contactName, contactEmail, plan }) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('onboarding_leads')
      .insert({
        source: 'manual',
        organization: organization || null,
        community_address: communityAddress || null,
        contact_name: contactName || null,
        contact_email: contactEmail || null,
        plan: plan || null,
        created_by: user?.id || null,
      })
      .select('id')
      .single();
    if (error) throw error;
    return data;
  },

  async listParcels(leadId) {
    const { data, error } = await supabase
      .from('onboarding_parcels')
      .select('id, confidence, selected, situs_address, unit_number, owner_name, owner_mailing_city, owner_mailing_state, subdivision, property_type, imported_property_id')
      .eq('lead_id', leadId)
      .order('confidence', { ascending: true })
      .order('situs_address', { ascending: true })
      .order('unit_number', { ascending: true })
      .range(0, 1999);
    if (error) throw error;
    return data || [];
  },

  async setParcelSelected(parcelId, selected) {
    const { error } = await supabase.from('onboarding_parcels').update({ selected }).eq('id', parcelId);
    if (error) throw error;
  },

  async setParcelsSelected(parcelIds, selected) {
    if (!parcelIds.length) return;
    const { error } = await supabase.from('onboarding_parcels').update({ selected }).in('id', parcelIds);
    if (error) throw error;
  },

  // addAddress: another address of the same association (merged associations, other
  // phases); all of the lead's addresses are researched again together.
  runResearch(leadId, addAddress) {
    return invokeFunction('research-hoa-parcels', addAddress ? { lead_id: leadId, add_address: addAddress } : { lead_id: leadId });
  },

  provision(payload) {
    return invokeFunction('provision-hoa', payload);
  },

  async importParcels(leadId) {
    const { data, error } = await supabase.rpc('import_onboarding_parcels', { p_lead_id: leadId });
    if (error) throw error;
    return data;
  },

  inviteFirstAdmin({ hoaId, email, fullName }) {
    return invokeFunction('invite-user', { email, full_name: fullName, role: 'admin', hoa_id: hoaId });
  },

  // Signs this staff member in on the other association's address (one-time token)
  // and goes there. The current tab leaves; nothing is shared between addresses.
  async switchTo(slug) {
    const { token_hash: tokenHash } = await invokeFunction('switch-hoa', { slug });
    window.location.assign(`${tenantPortalUrl(slug, '/switch')}#token_hash=${encodeURIComponent(tokenHash)}`);
  },
};

export default associationService;
